import { useState, useEffect } from 'react';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  orderBy,
  limit,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';

// Default flags structure (fallback if Firestore is empty)
const DEFAULT_FLAGS = {
  // Observability (Phase 0)
  enableOpenTelemetry: false,
  enableDetailedMetrics: false,
  enablePerformanceLogging: false,
  traceSampleRate: 0.1,

  // Caching (Phase 1)
  enableRedisCache: false,
  redisCacheTTL: 3600,
  cacheCurrentMonth: false,

  // Reliability (Phase 2)
  enableCircuitBreaker: false,
  circuitBreakerThreshold: 5,
  enableReconciliation: false,
  reconciliationTolerance: 0.01,
  autoFixReconciliation: false,

  // Scalability (Phase 3)
  enableCloudTasksFanout: false,
  fanoutThreshold: 5000,
  enableBigQueryExport: false,
  bigQueryDataset: 'commission_analytics',
  enableStreamingAggregation: false,

  // Performance Tuning
  maxBatchSize: 1000,
  firestoreTimeout: 30000,
  enableParallelAggregation: false,
};

// Helper to log changes to audit log
async function logFlagChange(
  action: 'enable' | 'disable' | 'update',
  featureName: string,
  oldValue: any,
  newValue: any,
  companyId?: string | null,
  companyName?: string | null,
  reason?: string
) {
  try {
    const user = auth.currentUser;
    await addDoc(collection(db, 'flag_audit_log'), {
      timestamp: serverTimestamp(),
      userId: user?.uid || 'unknown',
      userEmail: user?.email || 'unknown',
      action,
      featureName,
      companyId: companyId || null,
      companyName: companyName || null,
      oldValue,
      newValue,
      reason: reason || null,
    });
  } catch (err) {
    console.error('[Audit Log] Failed to log change:', err);
  }
}

/**
 * Hook for managing global feature flags
 */
export function useFeatureFlags() {
  const [globalFlags, setGlobalFlags] = useState<any>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const flagsDoc = await getDoc(doc(db, 'global_config', 'feature_flags'));

        if (flagsDoc.exists()) {
          const data = flagsDoc.data();
          setGlobalFlags({ ...DEFAULT_FLAGS, ...data });
        } else {
          // Initialize with defaults if doesn't exist
          await setDoc(doc(db, 'global_config', 'feature_flags'), DEFAULT_FLAGS);
          setGlobalFlags(DEFAULT_FLAGS);

          // Log initialization
          await logFlagChange(
            'update',
            'Global Defaults',
            undefined,
            DEFAULT_FLAGS,
            null,
            null,
            'Initialized default feature flags'
          );
        }
      } catch (err: any) {
        console.error('[Feature Flags] Error fetching flags:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFlags();
  }, []);

  const updateGlobalFlags = async (newFlags: any) => {
    try {
      const oldFlags = globalFlags;

      // Update Firestore
      await setDoc(doc(db, 'global_config', 'feature_flags'), newFlags, { merge: true });

      // Update local state
      setGlobalFlags(newFlags);

      // Log changes for each modified flag
      const changedFlags = Object.keys(newFlags).filter(
        key => oldFlags[key] !== newFlags[key]
      );

      for (const flagKey of changedFlags) {
        const action =
          typeof newFlags[flagKey] === 'boolean'
            ? newFlags[flagKey] ? 'enable' : 'disable'
            : 'update';

        await logFlagChange(
          action,
          flagKey,
          oldFlags[flagKey],
          newFlags[flagKey],
          null,
          null,
          'Updated global default'
        );
      }
    } catch (err: any) {
      console.error('[Feature Flags] Error updating flags:', err);
      throw err;
    }
  };

  return {
    globalFlags,
    loading,
    error,
    updateGlobalFlags,
  };
}

/**
 * Hook for managing company-specific flag overrides
 */
export function useCompanyFlags() {
  const [companies, setCompanies] = useState<any[]>([]);
  const [companiesWithFlags, setCompaniesWithFlags] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        // Fetch all companies
        const companiesSnapshot = await getDocs(collection(db, 'companies'));
        const companiesList = companiesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          ...doc.data(),
        }));

        setCompanies(companiesList);

        // Fetch companies with feature flag overrides
        const companiesWithOverrides = [];
        for (const company of companiesList) {
          const flagsDoc = await getDoc(
            doc(db, 'companies', company.id, 'config', 'feature_flags')
          );

          if (flagsDoc.exists()) {
            const flags = flagsDoc.data();
            const enabledFeatures: string[] = [];
            const disabledFeatures: string[] = [];

            Object.entries(flags).forEach(([key, value]) => {
              if (typeof value === 'boolean') {
                if (value) {
                  enabledFeatures.push(key);
                } else {
                  disabledFeatures.push(key);
                }
              }
            });

            companiesWithOverrides.push({
              id: company.id,
              name: company.name,
              enabledFeatures,
              disabledFeatures,
              lastModified: flags.lastModified || new Date().toISOString(),
              modifiedBy: flags.modifiedBy || 'unknown',
            });
          }
        }

        setCompaniesWithFlags(companiesWithOverrides);
      } catch (err: any) {
        console.error('[Company Flags] Error fetching companies:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  return {
    companies,
    companiesWithFlags,
    loading,
    error,
  };
}

/**
 * Hook for fetching audit log
 */
export function useAuditLog() {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const logsQuery = query(
          collection(db, 'flag_audit_log'),
          orderBy('timestamp', 'desc'),
          limit(100)
        );

        const logsSnapshot = await getDocs(logsQuery);
        const logsList = logsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate?.()?.toISOString() || new Date().toISOString(),
        }));

        setLogs(logsList);
      } catch (err: any) {
        console.error('[Audit Log] Error fetching logs:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  return {
    logs,
    loading,
    error,
  };
}

/**
 * Hook for calculating rollout progress
 */
export function useRolloutProgress() {
  const [progress, setProgress] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        // Fetch all companies
        const companiesSnapshot = await getDocs(collection(db, 'companies'));
        const totalCompanies = companiesSnapshot.size;

        // Count companies with each feature enabled
        const featureStats: Record<string, { enabled: number; total: number }> = {
          'Observability (Tracing & Metrics)': { enabled: 0, total: totalCompanies },
          'Redis Cache': { enabled: 0, total: totalCompanies },
          'Circuit Breaker': { enabled: 0, total: totalCompanies },
          'Cloud Tasks Fan-Out': { enabled: 0, total: totalCompanies },
        };

        for (const companyDoc of companiesSnapshot.docs) {
          const flagsDoc = await getDoc(
            doc(db, 'companies', companyDoc.id, 'config', 'feature_flags')
          );

          if (flagsDoc.exists()) {
            const flags = flagsDoc.data();
            if (flags.enableOpenTelemetry || flags.enableDetailedMetrics) {
              featureStats['Observability (Tracing & Metrics)'].enabled++;
            }
            if (flags.enableRedisCache) {
              featureStats['Redis Cache'].enabled++;
            }
            if (flags.enableCircuitBreaker) {
              featureStats['Circuit Breaker'].enabled++;
            }
            if (flags.enableCloudTasksFanout) {
              featureStats['Cloud Tasks Fan-Out'].enabled++;
            }
          }
        }

        // Build progress data
        const progressData = [
          {
            feature: 'Observability (Tracing & Metrics)',
            phase: 'Phase 0',
            enabled: featureStats['Observability (Tracing & Metrics)'].enabled,
            total: totalCompanies,
            percentage: (featureStats['Observability (Tracing & Metrics)'].enabled / totalCompanies) * 100,
            status: featureStats['Observability (Tracing & Metrics)'].enabled > 0 ? 'rolling' : 'planned',
            color: 'from-blue-600 to-cyan-600',
          },
          {
            feature: 'Redis Cache',
            phase: 'Phase 1',
            enabled: featureStats['Redis Cache'].enabled,
            total: totalCompanies,
            percentage: (featureStats['Redis Cache'].enabled / totalCompanies) * 100,
            status: featureStats['Redis Cache'].enabled > 0 ? 'rolling' : 'planned',
            color: 'from-green-600 to-teal-600',
          },
          {
            feature: 'Circuit Breaker',
            phase: 'Phase 2',
            enabled: featureStats['Circuit Breaker'].enabled,
            total: totalCompanies,
            percentage: (featureStats['Circuit Breaker'].enabled / totalCompanies) * 100,
            status: featureStats['Circuit Breaker'].enabled > 0 ? 'rolling' : 'planned',
            color: 'from-orange-600 to-red-600',
          },
          {
            feature: 'Cloud Tasks Fan-Out',
            phase: 'Phase 3',
            enabled: featureStats['Cloud Tasks Fan-Out'].enabled,
            total: totalCompanies,
            percentage: (featureStats['Cloud Tasks Fan-Out'].enabled / totalCompanies) * 100,
            status: featureStats['Cloud Tasks Fan-Out'].enabled > 0 ? 'rolling' : 'planned',
            color: 'from-purple-600 to-pink-600',
          },
        ];

        setProgress(progressData);
      } catch (err: any) {
        console.error('[Rollout Progress] Error calculating progress:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, []);

  return {
    progress,
    loading,
    error,
  };
}
